import { useCallback, useEffect, useState } from "react";
import { formatCurrency } from "helpers";
import { getFinanceExpensesAPI } from "services/api/reports/finance/api";
import { ExpenseBreakdown } from "services/api/reports/finance/types";
import { useDashboardDateRange } from "ui/modules/dashboard/pages/DashboardPage/context/DashboardDateRangeContext";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import ExpenseGroupRow from "./components/ExpenseGroupRow/ExpenseGroupRow";

const ExpenseBreakdownTab: React.FC = () => {
  const { range } = useDashboardDateRange();
  const [data, setData] = useState<ExpenseBreakdown | null>(null);
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getFinanceExpensesAPI(range.from, range.to)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, [range.from, range.to]);

  const handleToggle = useCallback((name: string) => {
    setOpenGroups((current) =>
      current.includes(name) ? current.filter((n) => n !== name) : [...current, name]
    );
  }, []);

  if (isLoading) {
    return (
      <div className="relative min-h-[200px]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  if (!data || data.groups.length === 0) {
    return (
      <div className="px-0 py-6 xl:px-6">
        <NoResults message="Nema troškova u odabranom periodu" />
      </div>
    );
  }

  return (
    <div className="px-0 py-6 xl:px-6 space-y-3">
      <div className="bg-white rounded-sm border">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 text-xs font-semibold uppercase text-slate-400">
          <span className="w-4" />
          <span className="flex-1">Vrsta troška</span>
          <span className="w-16 text-right">Broj</span>
          <span className="w-32 text-right">Iznos</span>
          <span className="w-16 text-right">Udio</span>
          <span className="w-24" />
        </div>
        {data.groups.map((group) => (
          <ExpenseGroupRow
            key={group.name}
            node={group}
            isOpen={openGroups.includes(group.name)}
            onToggle={handleToggle}
          />
        ))}
        <div className="flex items-center gap-3 px-4 py-3 border-t border-slate-200 text-sm font-bold">
          <span className="w-4" />
          <span className="flex-1">Ukupno</span>
          <span className="w-16" />
          <span className="w-32 text-right">{formatCurrency.format(data.total)}</span>
          <span className="w-16" />
          <span className="w-24" />
        </div>
      </div>
      <div className="text-xs text-slate-400">
        Nabavna vrijednost vozila nije uključena — ona se prati zasebno u Sažetku.
      </div>
    </div>
  );
};

export default ExpenseBreakdownTab;
